import { Badge, Button, Group, Paper, Stack, Text } from "@mantine/core"
import { useEffect, useState } from "react"
import { IconArrowLeft, appIcon } from "atlas_forecasting_app/components/common/icons"
import { CenteredMessage, EmptyState, ErrorAlert, LoadingState, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { formatDateTime } from "atlas_forecasting_app/lib/format"
import { RouterLink } from "atlas_forecasting_app/lib/router"

const FILTER_OPTIONS = [
  { value: "all", label: "All forecasts" },
  { value: "failed", label: "Failed only" },
  { value: "failed_unreviewed", label: "Failed, unreviewed" },
]

function statusColor(status) {
  switch (status) {
    case "completed":
      return "green"
    case "failed":
      return "red"
    case "running":
      return "orange"
    default:
      return "gray"
  }
}

function statusLabel(status) {
  switch (status) {
    case "completed":
      return "Succeeded"
    case "failed":
      return "Failed"
    case "running":
      return "Running"
    default:
      return "Queued"
  }
}

function emptyMessage(filter) {
  switch (filter) {
    case "failed":
      return "No failed forecasts found."
    case "failed_unreviewed":
      return "No failed forecasts are awaiting review."
    default:
      return "No forecasts found."
  }
}

export function AdminForecastsPage({ currentUser }) {
  const isAdmin = !!currentUser?.roles?.includes("admin")
  const [items, setItems] = useState([])
  const [cursor, setCursor] = useState(null)
  const [filter, setFilter] = useState("all")
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [reviewingId, setReviewingId] = useState(null)
  const [error, setError] = useState("")

  const loadForecasts = async ({ reset = false } = {}) => {
    if (!isAdmin) return
    if (reset) {
      setLoading(true)
    } else {
      if (!cursor || loadingMore) return
      setLoadingMore(true)
    }

    try {
      const qs = new URLSearchParams()
      qs.set("limit", "25")
      qs.set("filter", filter)
      if (!reset && cursor) qs.set("cursor", cursor)

      const data = await apiFetch(`/api/admin/forecasts?${qs.toString()}`)
      const forecasts = data.forecasts || []
      setItems((prev) => (reset ? forecasts : prev.concat(forecasts)))
      setCursor(data.next_cursor || null)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    setItems([])
    setCursor(null)
    setError("")
    loadForecasts({ reset: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, isAdmin])

  const markReviewed = async (forecastId) => {
    if (reviewingId) return
    setReviewingId(forecastId)

    try {
      const data = await apiFetch(`/api/admin/forecasts/${forecastId}/review`, { method: "PATCH", body: {} })
      const reviewedForecast = data?.forecast
      if (!reviewedForecast) return

      setItems((prev) => (
        filter === "failed_unreviewed"
          ? prev.filter((forecast) => forecast.id !== forecastId)
          : prev.map((forecast) => (forecast.id === forecastId ? reviewedForecast : forecast))
      ))
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setReviewingId(null)
    }
  }

  if (!currentUser) {
    return <CenteredMessage title="Admin" body="Log in required." />
  }

  if (!isAdmin) {
    return <CenteredMessage title="Admin" body="You don’t have access to this page." />
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Forecast review"
        subtitle="Inspect forecast outcomes and triage failures."
        action={
          <Button component={RouterLink} to="/admin" variant="light" leftSection={appIcon(IconArrowLeft, "inline")}>
            Back to admin
          </Button>
        }
      />

      <SectionCard title="Filters" subtitle="Focus on failures that still need admin attention.">
        <Group gap="sm">
          {FILTER_OPTIONS.map((option) => (
            <Button
              key={option.value}
              type="button"
              variant={filter === option.value ? "filled" : "light"}
              onClick={() => setFilter(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </Group>
      </SectionCard>

      <ErrorAlert message={error} />

      {loading ? <LoadingState label="Loading forecasts..." /> : null}
      {!loading && items.length === 0 && !error ? <EmptyState message={emptyMessage(filter)} /> : null}

      <Stack gap="md">
        {items.map((forecast) => {
          const requesterLabel = forecast.requested_by_user?.email || (
            forecast.requested_by_user?.id != null ? `User #${forecast.requested_by_user.id}` : "Unknown user"
          )
          const reviewerLabel = forecast.failure_reviewed_by_user?.email || (
            forecast.failure_reviewed_by_user?.id != null ? `User #${forecast.failure_reviewed_by_user.id}` : "another admin"
          )

          return (
            <Paper key={forecast.id} withBorder radius="md" shadow="xs" p="lg">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start" gap="md">
                  <Stack gap={4}>
                    <Text fw={600}>{forecast.question?.title || `Forecast #${forecast.id}`}</Text>
                    <Text size="sm" c="dimmed">
                      Forecast #{forecast.id}
                      {requesterLabel ? ` • requested by ${requesterLabel}` : ""}
                      {forecast.created_at ? ` • created ${formatDateTime(forecast.created_at)}` : ""}
                    </Text>
                  </Stack>

                  <Group gap="xs">
                    <Badge variant="outline">{forecast.question?.question_type || "forecast"}</Badge>
                    <Badge variant="light" color={statusColor(forecast.status)}>
                      {statusLabel(forecast.status)}
                    </Badge>
                    {forecast.status === "failed" ? (
                      <Badge variant="light" color={forecast.failure_reviewed_at ? "teal" : "yellow"}>
                        {forecast.failure_reviewed_at ? "Reviewed" : "Needs review"}
                      </Badge>
                    ) : null}
                  </Group>
                </Group>

                {forecast.completed_at ? (
                  <Text size="sm" c="dimmed">
                    Finished {formatDateTime(forecast.completed_at)}
                  </Text>
                ) : null}

                {forecast.status === "failed" ? (
                  <Paper withBorder radius="sm" bg="red.0" p="md">
                    <Stack gap="xs">
                      <Text size="sm" fw={600} c="red.9">
                        Failure details
                      </Text>
                      <Text size="sm" c="red.9">
                        {forecast.error_message || "No error message recorded."}
                      </Text>
                      {forecast.failure_reviewed_at ? (
                        <Text size="sm" c="dimmed">
                          Reviewed by {reviewerLabel} on {formatDateTime(forecast.failure_reviewed_at)}.
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Mark this failure as reviewed after the issue has been triaged.
                        </Text>
                      )}
                    </Stack>
                  </Paper>
                ) : null}

                <Group justify="space-between" align="center" gap="md">
                  <Group gap="xs">
                    <Button component={RouterLink} to={`/forecasts/${forecast.id}`} variant="light">
                      Open forecast
                    </Button>
                    <Button component={RouterLink} to={`/questions/${forecast.question.id}`} variant="subtle">
                      Open question
                    </Button>
                  </Group>

                  {forecast.status === "failed" && !forecast.failure_reviewed_at ? (
                    <Button
                      type="button"
                      color="teal"
                      variant="light"
                      disabled={reviewingId === forecast.id}
                      onClick={() => markReviewed(forecast.id)}
                    >
                      {reviewingId === forecast.id ? "Marking..." : "Mark reviewed"}
                    </Button>
                  ) : null}
                </Group>
              </Stack>
            </Paper>
          )
        })}

        {cursor ? (
          <Group justify="center">
            <Button type="button" variant="light" disabled={loadingMore} onClick={() => loadForecasts()}>
              {loadingMore ? "Loading..." : "Load more"}
            </Button>
          </Group>
        ) : null}
      </Stack>
    </Stack>
  )
}
